(function () {
  "use strict";

  const ROOT_ID = "m7WrapEditor";
  const FORM_PROPERTY_NAMES = {
    designId: "Design ID",
    designName: "Design Name",
    previewUrl: "Design Preview",
    buildUrl: "Production Build Sheet",
    jsonUrl: "Design Data",
    editUrl: "Edit Design"
  };
  const uploadNames = ["File 1", "File 2", "File 3"];
  function root() { return document.getElementById(ROOT_ID); }
  function endpoint() { return String(root()?.dataset.m7HandoffEndpoint || "").replace(/\/$/, ""); }
  function isConfigured() { return /^https:\/\//i.test(endpoint()); }

  async function prepare() {
    if (!isConfigured()) throw new Error("The design storage endpoint has not been configured.");
    if (!window.M7Editor?.getSnapshot) throw new Error("The editor build data is not ready.");

    showStatus("SAVING YOUR DESIGN", "Creating your permanent preview and production build sheet...");
    try {
      const editor = normalizeEditorSnapshot(window.M7Editor.getSnapshot());
      const vehicle = collectVehicleDetails();
      const previewDataUrl = await capturePreview();
      const payload = {
        previewDataUrl,
        build: {
          designName: editor.designName,
          product: {
            id: root()?.dataset.productId || "",
            handle: root()?.dataset.productHandle || "",
            title: root()?.dataset.productTitle || document.title
          },
          editor,
          vehicle,
          sourceUrl: location.href
        }
      };

      const response = await fetch(`${endpoint()}/api/designs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const record = await response.json().catch(() => ({}));
      if (!response.ok || !record.designId) throw new Error(record.error || "The design service did not return a Design ID.");

      try {
        if (typeof window.M7SaveAccountSnapshot === "function") {
          await window.M7SaveAccountSnapshot(editor, { silent: true });
        }
      } catch (_) {
        // The permanent order design is already saved. Account-copy failure
        // must not turn a successful order handoff into a false save failure.
      }
      saveEditableCopy(editor);
      attachToCart(record, editor.designName, editor, vehicle);
      localStorage.setItem("m7-last-order-design", JSON.stringify({ ...record, editor, designName: editor.designName, savedAt: Date.now() }));
      showStatus("DESIGN SAVED", `${record.designId} is attached to this order.`, true);
      return record;
    } catch (error) {
      hideStatus();
      throw error;
    }
  }

  async function capturePreview() {
    const source = document.querySelector(".sled-svg-wrapper svg");
    if (!source) throw new Error("The sled preview has not finished loading.");
    const clone = source.cloneNode(true);
    clone.removeAttribute("width");
    clone.removeAttribute("height");
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.querySelectorAll(".m7-glow").forEach(node => node.classList.remove("m7-glow"));

    const viewBox = clone.getAttribute("viewBox")?.split(/\s+/).map(Number);
    if (!viewBox || viewBox.length !== 4 || viewBox.some(Number.isNaN)) throw new Error("The sled artwork has an invalid viewBox.");
    const [, , width, height] = viewBox;
    const canvas = document.createElement("canvas");
    canvas.width = 1600;
    canvas.height = 933;
    const context = canvas.getContext("2d");
    context.fillStyle = "#171a1e";
    context.fillRect(0, 0, canvas.width, canvas.height);

    const serialized = new XMLSerializer().serializeToString(clone);
    const image = await loadSvgImage(serialized);
    const scale = Math.min((canvas.width - 90) / width, (canvas.height - 90) / height);
    const drawWidth = width * scale;
    const drawHeight = height * scale;
    context.drawImage(image, (canvas.width - drawWidth) / 2, (canvas.height - drawHeight) / 2, drawWidth, drawHeight);
    return canvas.toDataURL("image/webp", 0.84);
  }

  function loadSvgImage(svg) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
      const image = new Image();
      image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
      image.onerror = () => { URL.revokeObjectURL(url); reject(new Error("The clean design preview could not be rendered.")); };
      image.src = url;
    });
  }

  function cleanLabel(value) {
    return String(value || "").replace(/\s*\*\s*$/, "").replace(/\s+/g, " ").trim();
  }

  function displayValue(field) {
    if (!field || field.disabled) return "";
    if ((field.type === "radio" || field.type === "checkbox") && !field.checked) return "";
    if (field.type === "checkbox") return "Yes";
    if (field.type === "file") return [...(field.files || [])].map(file => file.name).join(", ");
    if (field.tagName === "SELECT") return cleanLabel(field.selectedOptions?.[0]?.textContent || field.value);
    if (field.type === "radio") {
      const row = field.closest(".mw-option__value,label,.form-check,.radio");
      return cleanLabel(row?.querySelector("label")?.textContent || row?.textContent || field.value)
        .replace(/\(\s*[+-]?\$[^)]*\)/g, "").trim();
    }
    return String(field.value || "").trim();
  }

  function fieldLabel(field) {
    const group = field.closest("[data-option-id],.form-group,.mw-option,.field");
    return cleanLabel(group?.querySelector(".mw-text--style-strong,.mw-option__label,.mw-option__title,legend,label")?.textContent || field.dataset.apoField || field.name);
  }

  function collectVehicleDetails() {
    const result = {};
    document.querySelectorAll("[data-apo-field],.mw-apo-container input,.mw-apo-container select,.mw-apo-container textarea").forEach(field => {
      const label = fieldLabel(field);
      const value = displayValue(field);
      if (!label || !value || /^(coverage|trim level|vinyl type|laminate finish|laminate effect|design |file [123])/i.test(label)) return;
      result[label] = value;
    });
    return result;
  }

  function optionByName(names) {
    const wanted = names.map(name => name.toLowerCase());
    return [...document.querySelectorAll(".mw-apo-container [data-option-id]")].find(option => wanted.includes(cleanLabel(option.querySelector(".mw-text--style-strong,.mw-option__label,.mw-option__title,legend,label")?.textContent).toLowerCase()));
  }

  function selectedOptionLabel(names, fallback) {
    const option = optionByName(names);
    const field = option?.querySelector("input:checked,select,textarea,input:not([type=hidden]):not([type=radio]):not([type=checkbox])");
    return displayValue(field) || fallback || "";
  }

  function normalizeCssColor(value) {
    const probe = document.createElement("span");
    probe.style.color = "";
    probe.style.color = String(value || "").trim();
    if (!probe.style.color) return String(value || "");
    document.body.appendChild(probe);
    const rgb = getComputedStyle(probe).color;
    probe.remove();
    const parts = rgb.match(/[\d.]+/g)?.slice(0, 3).map(Number);
    return parts?.length === 3 ? `#${parts.map(n => Math.round(n).toString(16).padStart(2, "0")).join("").toUpperCase()}` : String(value || "");
  }

  function colorCatalog() {
    const map = new Map(Object.entries({
      "#090A0C":"M7 Black","#202327":"Graphite","#3B3D41":"Gunmetal","#70757C":"Medium Silver","#B8BDC4":"Light Silver","#FFFFFF":"White","#B9FF19":"M7 Hi-Vis Lime","#78D600":"Factory Green","#087B55":"Deep Emerald","#00B887":"Teal Green","#00ACD4":"Cyan Blue","#63DDFF":"Ice Blue","#0E2C5D":"Deep Navy","#245CFF":"Factory Blue","#6C83FF":"Periwinkle Blue","#5A21A8":"Deep Violet","#9A2CFF":"Electric Purple","#EF4DFF":"Magenta","#DF1D3C":"Crimson Red","#FF4D00":"Red Orange","#FF7B00":"Factory Orange","#FFAD00":"Amber","#F4DC3C":"Factory Yellow","#9B6A3C":"Bronze",
      "#0D2451":"Deep Polaris Navy","#183C7D":"Polaris Blue","#0C1017":"M7 Black","#202838":"Blue Graphite","#4563A2":"Steel Blue","#98A5B8":"Cool Silver","#121722":"Midnight Navy",
      "#090909":"M7 Black","#232323":"Graphite","#111111":"Black","#343434":"Gunmetal","#FF7A00":"Factory Orange","#787878":"Medium Silver","#171717":"Black","#D62B00":"Ember Red","#EEEEEE":"Arctic White",
      "#101629":"Midnight Blue","#203562":"Arctic Navy","#9B2CFF":"Electric Violet","#D7F6FF":"Ice White","#26243C":"Violet Graphite","#6764A3":"Muted Violet","#8CE9FF":"Arctic Ice Blue","#181B2C":"Midnight Graphite","#7022D1":"Deep Electric Violet","#F8FDFF":"Snow White",
      "#212324":"Graphite","#3C3D3F":"Gunmetal","#494B4D":"Dark Silver","#57595A":"Medium Silver","#2E3032":"Charcoal","#0B1110":"Deep Black Green","#3B241A":"Dark Bronze","#0C1117":"Midnight Black","#120D18":"Black Violet","#101214":"Carbon Black","#0B0D12":"M7 Black"
    }));
    document.querySelectorAll("[data-m7-color][data-name],[data-m7-end][data-name]").forEach(node => {
      [node.dataset.m7Color, node.dataset.m7End].filter(Boolean).forEach(color => map.set(normalizeCssColor(color), node.dataset.name));
    });
    return map;
  }

  function rgbFromHex(hex) {
    const match = String(hex || "").match(/^#([0-9a-f]{6})$/i);
    if (!match) return null;
    const value = Number.parseInt(match[1], 16);
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  }

  function descriptiveColor(hex) {
    const rgb = rgbFromHex(hex);
    if (!rgb) return "Custom Color";
    const [r, g, b] = rgb.map(value => value / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b), light = (max + min) / 2;
    const delta = max - min;
    let hue = 0;
    if (delta) {
      hue = max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
      hue = (hue * 60 + 360) % 360;
    }
    const saturation = delta ? delta / (1 - Math.abs(2 * light - 1)) : 0;
    if (light < .075) return "Custom Black";
    if (saturation < .08) return light > .92 ? "Custom White" : light > .7 ? "Custom Light Silver" : light > .42 ? "Custom Silver" : light > .2 ? "Custom Graphite" : "Custom Charcoal";
    const tone = light < .2 ? "Deep " : light > .72 ? "Light " : saturation < .35 ? "Muted " : "";
    const family = hue < 15 || hue >= 345 ? "Red" : hue < 40 ? "Orange" : hue < 65 ? "Yellow" : hue < 155 ? "Green" : hue < 190 ? "Teal" : hue < 250 ? "Blue" : hue < 285 ? "Violet" : hue < 330 ? "Magenta" : "Rose";
    return `Custom ${tone}${family}`;
  }

  function nearestCatalogName(hex, catalog) {
    const target = rgbFromHex(hex);
    if (!target) return "";
    let best = null;
    catalog.forEach((name, candidate) => {
      const rgb = rgbFromHex(candidate);
      if (!rgb) return;
      const distance = Math.sqrt(rgb.reduce((sum, value, index) => sum + (value - target[index]) ** 2, 0));
      if (!best || distance < best.distance) best = { name, distance };
    });
    return best?.distance <= 12 ? best.name : "";
  }

  function readableColor(value) {
    const catalog = colorCatalog();
    return String(value || "").split(" -> ").map(color => {
      const normalized = normalizeCssColor(color);
      const editorCandidate = cleanLabel(window.M7EditorColorName?.(color));
      const editorName = editorCandidate && !/^(?:#|rgba?\(|hsla?\()/i.test(editorCandidate) ? editorCandidate : "";
      const name = editorName || catalog.get(normalized) || nearestCatalogName(normalized, catalog) || descriptiveColor(normalized);
      return `${name} - ${normalized}`;
    }).join(" -> ");
  }

  function normalizeEditorSnapshot(editor) {
    const activeZones = new Set(editor.activeZones || []);
    editor.paints = Object.fromEntries(Object.entries(editor.paints || {}).filter(([key]) => {
      return activeZones.has(key) || key.startsWith("PART_");
    }));
    editor.optionLabels = {
      coverage: selectedOptionLabel(["Coverage", "Wrap Coverage", "Trim Level"], editor.options?.coverage),
      vinylType: selectedOptionLabel(["Vinyl Type"], editor.options?.vinylType),
      laminateFinish: selectedOptionLabel(["Laminate Finish"], editor.options?.laminateFinish),
      laminateEffect: selectedOptionLabel(["Laminate Effect"], editor.options?.laminateEffect)
    };
    Object.values(editor.paints || {}).forEach(paint => {
      paint.a = normalizeCssColor(paint.a);
      if (paint.b) paint.b = normalizeCssColor(paint.b);
      paint.nameA = readableColor(paint.a);
      if (paint.b) paint.nameB = readableColor(paint.b);
    });
    return editor;
  }

  function saveEditableCopy(editor) {
    try {
      const saved = JSON.parse(localStorage.getItem("m7-wrap-designs") || "[]");
      const copy = {
        v: 4,
        name: editor.designName,
        paints: editor.paints,
        options: editor.options
      };
      const updated = [copy, ...saved.filter(item => item && item.name !== copy.name)].slice(0, 12);
      localStorage.setItem("m7-wrap-designs", JSON.stringify(updated));
    } catch (_) {}
  }

  function attachToCart(record, designName, editor, vehicle) {
    const values = { ...record, designName, editUrl: location.origin + location.pathname };
    const colorValues = {};
    (editor.activeZones || []).forEach((zone, index) => {
      const paint = editor.paints?.[zone];
      if (!paint) return;
      colorValues[`Wrap Color ${index + 1}`] = paint.mode === "gradient"
        ? `${readableColor(paint.a)} -> ${readableColor(paint.b)}`
        : readableColor(paint.a);
    });
    const forms = findProductForms();
    if (!forms.length) throw new Error("The Shopify product form could not be found.");
    forms.forEach(form => {
      Object.entries(FORM_PROPERTY_NAMES).forEach(([key, label]) => upsertProperty(form, label, values[key] || ""));
      Object.entries(colorValues).forEach(([label, value]) => upsertProperty(form, label, value));
      Object.entries(editor.optionLabels || {}).forEach(([key, value]) => upsertProperty(form, ({coverage:"Coverage",vinylType:"Vinyl Type",laminateFinish:"Laminate Finish",laminateEffect:"Laminate Effect"})[key], value));
      Object.entries(vehicle || {}).forEach(([label, value]) => upsertProperty(form, label, value));
    });

    if (window.M7Mageworx?.setValue) {
      Object.entries(FORM_PROPERTY_NAMES).forEach(([key, label]) => {
        try { window.M7Mageworx.setValue(label, values[key] || "", label); } catch (_) {}
      });
    }
  }

  function findProductForms() {
    const forms = new Set();
    document.querySelectorAll('form[action*="/cart/add"], .mw-apo-container form').forEach(form => forms.add(form));
    const button = document.querySelector(".mw-apo-add-to-cart-custom");
    if (button?.form) forms.add(button.form);
    return [...forms];
  }

  function upsertProperty(form, label, value) {
    const name = `properties[${label}]`;
    let input = [...form.querySelectorAll('input[type="hidden"]')].find(node => node.name === name);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.dataset.m7OrderProperty = "true";
      form.appendChild(input);
    }
    input.value = value;
  }

  function showStatus(title, message, complete) {
    clearTimeout(showStatus.hideTimer);
    let overlay = document.getElementById("m7OrderStatus");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "m7OrderStatus";
      overlay.innerHTML = '<div class="m7-order-status-card"><span></span><b></b><p></p></div>';
      document.body.appendChild(overlay);
    }
    overlay.querySelector("b").textContent = title;
    overlay.querySelector("p").textContent = message;
    overlay.querySelector("span").textContent = complete ? "OK" : "";
    overlay.classList.toggle("complete", !!complete);
    overlay.classList.add("open");
    if (complete) showStatus.hideTimer = setTimeout(hideStatus, 900);
  }

  function hideStatus() { document.getElementById("m7OrderStatus")?.classList.remove("open"); }

  async function cartCount() {
    const response = await fetch("/cart.js", { headers: { Accept: "application/json" }, cache: "no-store" });
    if (!response.ok) throw new Error("The Shopify cart could not be checked.");
    return (await response.json()).item_count || 0;
  }

  async function syncApoFields() {
    const controls = [...document.querySelectorAll("#dynamic-popup-content [data-apo-field]")];
    for (const control of controls) {
      if (control.closest(".form-group")?.classList.contains("hidden")) {
        const option = document.querySelector(`.mw-apo-container [data-option-id="${CSS.escape(control.dataset.apoField || '')}"]`);
        const neutralRow = [...(option?.querySelectorAll('.mw-option__value[data-value-id]') || [])]
          .find(row => /^(?:n\/?a|not applicable|none|no)\b/i.test(cleanLabel(row.querySelector('.mw-option__value-text')?.textContent || row.textContent)));
        const neutralSelect = [...(option?.querySelectorAll('select option[value]') || [])]
          .find(item => item.value && /^(?:n\/?a|not applicable|none|no)\b/i.test(cleanLabel(item.textContent)));
        const neutralValue = neutralRow?.dataset.valueId || neutralSelect?.value;
        if (neutralValue) window.M7Mageworx?.setValue?.(control.dataset.apoField, neutralValue, fieldLabel(control));
        continue;
      }
      if ((control.type === "radio" || control.type === "checkbox") && !control.checked) continue;
      const id = control.dataset.apoField;
      if (!id || !control.value) continue;
      if (control.type === "radio" || control.type === "checkbox") {
        window.M7Mageworx?.setRadio?.(id, control.value);
      } else {
        window.M7Mageworx?.setValue?.(id, control.value, fieldLabel(control));
      }
    }
    await window.M7Mageworx?.settle?.();
  }

  function apoValidationIssues() {
    const issues = new Set();
    document.querySelectorAll('.mw-apo-container [data-option-id]').forEach(option => {
      if (option.classList.contains('mw-hidden') || option.closest('.mw-hidden')) return;
      const proxy = document.querySelector(`#dynamic-popup-content [data-apo-field="${CSS.escape(option.dataset.optionId || '')}"]`);
      if (proxy?.closest('.form-group')?.classList.contains('hidden')) return;
      const label = cleanLabel(option.querySelector('.mw-text--style-strong,.mw-option__label,.mw-option__title,legend,label')?.textContent);
      const visibleError = [...option.querySelectorAll('.mw-text--style-negative[data-helptext-type]')]
        .find(node => {
          if (node.classList.contains('mw-hidden') || getComputedStyle(node).display === 'none') return false;
          const type = node.dataset.helptextType || '';
          const message = cleanLabel(node.textContent);
          if (!message || /^(none|optional)$/i.test(message)) return false;
          if (type === 'required') return true;
          const hasValue = !!option.querySelector('input:checked,select option:checked:not([value=""]),textarea:not(:placeholder-shown),input:not([type="file"]):not([type="radio"]):not([type="checkbox"]):not([value=""])');
          return hasValue && /^(length_range|number_range|selection_limit|file_count_range)$/.test(type);
        });
      if (visibleError && label) issues.add(label);
    });
    return [...issues];
  }

  async function addToCart() {
    const before = await cartCount();
    const button = [...document.querySelectorAll(".mw-apo-add-to-cart-custom")].find(node => !node.disabled);
    if (!button) throw new Error("The APO cart button is not available.");
    showStatus("ADDING TO CART", "APO is applying your configured options and pricing...");
    await syncApoFields();
    button.click();
    for (let attempt = 0; attempt < 25; attempt++) {
      await new Promise(done => setTimeout(done, 400));
      if (await cartCount() > before) {
        location.assign("/cart");
        return true;
      }
    }
    hideStatus();
    const issues = apoValidationIssues();
    if (issues.length) throw new Error(`APO needs this option corrected: ${issues.join(", ")}. Nothing was added.`);
    throw new Error("APO rejected the configured product without identifying an option. Nothing was added at the base price.");
  }

  function addStyles() {
    if (document.getElementById("m7OrderStatusStyles")) return;
    const style = document.createElement("style");
    style.id = "m7OrderStatusStyles";
    style.textContent = '#m7OrderStatus{position:fixed;inset:0;z-index:2147483600;background:#080a0dcc;display:none;place-items:center;padding:20px}#m7OrderStatus.open{display:grid}.m7-order-status-card{width:min(430px,92vw);background:#11151a;border:1px solid #3b424b;border-top:4px solid #b9ff19;color:#fff;padding:32px;text-align:center;box-shadow:0 24px 80px #000}.m7-order-status-card span{display:block;width:42px;height:42px;margin:0 auto 16px;border:3px solid #39404a;border-top-color:#b9ff19;border-radius:50%;animation:m7OrderSpin .8s linear infinite;font:800 24px/36px Arial}.m7-order-status-card b{display:block;font:800 20px/1.2 Arial;letter-spacing:1.5px}.m7-order-status-card p{color:#aab1ba;font:15px/1.5 Arial;margin:10px 0 0}#m7OrderStatus.complete .m7-order-status-card span{border-color:#b9ff19;color:#b9ff19;animation:none}@keyframes m7OrderSpin{to{transform:rotate(360deg)}}.m7-upload-group{border-top:1px solid #343a42;margin-top:18px;padding-top:18px}.m7-upload-group>label{display:block;margin-bottom:5px}.m7-upload-help{color:#9299a2;font-size:13px;margin:0 0 12px}.m7-upload-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}.m7-upload-slot{display:flex;align-items:center;justify-content:center;min-height:76px;border:1px dashed #59616b;border-radius:5px;background:#101419;color:#fff;cursor:pointer;padding:10px;text-align:center}.m7-upload-slot:hover{border-color:#b9ff19}.m7-upload-slot input{position:absolute;opacity:0;pointer-events:none}.m7-upload-slot b{display:block;color:#b9ff19;font-size:12px;letter-spacing:.08em}.m7-upload-slot small{display:block;margin-top:5px;color:#aab1ba;overflow-wrap:anywhere}@media(max-width:620px){.m7-upload-grid{grid-template-columns:1fr}}';
    document.head.appendChild(style);
  }

  function mageworxFiles() {
    return [...document.querySelectorAll('.mw-apo-container input[type="file"], #mw-apo-app-block input[type="file"]')]
      .filter((field, index, all) => !field.dataset.m7Proxy && all.indexOf(field) === index)
      .slice(0, 3);
  }

  function addMageworxUploads() {
    const content = document.getElementById("dynamic-popup-content");
    if (!content || content.querySelector(".m7-upload-group")) return;
    const logos = [...content.querySelectorAll(".form-group")].find(group => /mainstream logos/i.test(group.textContent));
    if (!logos) return;
    const group = document.createElement("div");
    group.className = "form-group m7-upload-group";
    group.innerHTML = '<label>Upload Logos / Reference Files</label><p class="m7-upload-help">Optional - upload up to 3 files. These are attached through Mageworx.</p><div class="m7-upload-grid"></div>';
    const grid = group.querySelector(".m7-upload-grid");
    uploadNames.forEach((name, index) => {
      const slot = document.createElement("label");
      slot.className = "m7-upload-slot";
      slot.innerHTML = `<input type="file" data-m7-proxy="${index}"><span><b>+ ${name}</b><small>Choose file</small></span>`;
      const proxy = slot.querySelector("input");
      proxy.addEventListener("change", () => {
        const native = mageworxFiles()[index];
        const file = proxy.files?.[0];
        if (!native) {
          proxy.value = "";
          alert(`Mageworx upload field ${index + 1} was not found. Please confirm all three file-upload options are enabled for this product.`);
          return;
        }
        if (!file) return;
        try {
          const transfer = new DataTransfer();
          transfer.items.add(file);
          native.files = transfer.files;
          native.dispatchEvent(new Event("input", { bubbles: true }));
          native.dispatchEvent(new Event("change", { bubbles: true }));
          slot.querySelector("small").textContent = file.name;
          slot.classList.add("selected");
        } catch (_) {
          proxy.value = "";
          alert("That file could not be passed to Mageworx. Please try it again.");
        }
      });
      grid.appendChild(slot);
    });
    logos.insertAdjacentElement("afterend", group);
  }

  function popupLabel(group) {
    return (group?.querySelector("label")?.textContent || "").replace(/\s*\*\s*$/, "").trim();
  }

  function popupGroup(pattern) {
    return [...document.querySelectorAll("#dynamic-popup-content .form-group")]
      .find(group => pattern.test(popupLabel(group)));
  }

  function selectedText(group) {
    const select = group?.querySelector("select");
    if (select) return select.options[select.selectedIndex]?.text || "";
    const checked = group?.querySelector('input[type="radio"]:checked');
    return checked?.closest("label")?.textContent || checked?.parentElement?.textContent || "";
  }

  function updatePopupConditions() {
    const brand = popupGroup(/^brand$/i);
    const isSkiDoo = /ski[\s-]*doo/i.test(selectedText(brand));
    [...document.querySelectorAll("#dynamic-popup-content .form-group")].forEach(group => {
      if (/turbo|storage|sled box|pull handle/i.test(popupLabel(group))) {
        group.classList.toggle("hidden", !isSkiDoo);
      }
    });

    const deck = popupGroup(/sled deck(?!.*measurement)|add.*sled deck/i);
    const measurements = popupGroup(/sled deck.*measurement/i);
    if (measurements) measurements.classList.toggle("hidden", !/yes|add|include/i.test(selectedText(deck)));
  }

  function wirePopupConditions() {
    const popup = document.getElementById("dynamic-popup-content");
    if (!popup || popup.dataset.m7ConditionsWired) return;
    popup.dataset.m7ConditionsWired = "true";
    popup.addEventListener("change", event => {
      if (event.target.matches("select,input[type=radio]")) updatePopupConditions();
    });
    updatePopupConditions();
  }

  function watchPopup() {
    const popup = document.getElementById("dynamic-popup-content");
    if (!popup) return;
    new MutationObserver(() => { addMageworxUploads(); wirePopupConditions(); updatePopupConditions(); })
      .observe(popup, { childList: true, subtree: true });
    addMageworxUploads();
    wirePopupConditions();
  }

  addStyles();
  document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", watchPopup) : watchPopup();
  window.M7OrderHandoff = { prepare, isConfigured, capturePreview, addToCart };
})();

/* M7 Mageworx V2 bridge: label discovery, coverage-scoped materials, compact cards. */
(function () {
  "use strict";

  function optionLabel(option) {
    return (option?.querySelector(".mw-text--style-strong,.mw-option__label,.mw-option__title,label,legend")?.textContent || "")
      .replace(/\s*\*\s*$/, "").trim();
  }
  function optionNamed(names) {
    for (const name of names) {
      const exact = M7Mageworx.optionFor("", name);
      if (exact) return exact;
    }
    const wanted = names.map(name => name.toLowerCase());
    const options = [...document.querySelectorAll(".mw-apo-container [data-option-id]")];
    return options.find(option => wanted.includes(optionLabel(option).toLowerCase())) ||
      options.find(option => /coverage|trim level/i.test(optionLabel(option)));
  }
  function choose(option, value) {
    const radio = option?.querySelector(`input[value="${CSS.escape(value)}"]`);
    if (!radio) return false;
    if (!radio.checked) (option.querySelector(`label[for="${radio.id}"]`) || radio).click();
    return radio.checked;
  }
  function currentScope() {
    const selected = state.apoData.coverage.find(item => item.id === state.customization.coverage) ||
      state.apoData.coverage.find(item => item.selected);
    return /\bhood\s*only\b/i.test(selected?.text || "") ? "hood" : "full";
  }
  function scoped(items) {
    const scope = currentScope();
    return items.filter(item => {
      const text = item.text.toLowerCase();
      if (!/full wrap|hood only/.test(text)) return true;
      return scope === "hood" ? text.includes("hood only") : text.includes("full wrap");
    });
  }
  function resetScopedChoices() {
    const vinyl = M7Mageworx.optionFor("", "Vinyl Type");
    const effect = M7Mageworx.optionFor("", "Laminate Effect");
    const standard = [...(vinyl?.querySelectorAll(".mw-option__value[data-value-id]") || [])]
      .find(row => /standard print/i.test(row.textContent));
    const none = [...(effect?.querySelectorAll(".mw-option__value[data-value-id]") || [])]
      .find(row => /^\s*none\b/i.test(row.textContent));
    if (standard) choose(vinyl, standard.dataset.valueId);
    if (none) choose(effect, none.dataset.valueId);
    state.customization.vinylType = standard?.dataset.valueId || "";
    state.customization.laminateEffect = none?.dataset.valueId || "";
  }

  const originalParseSelectOptions = parseSelectOptions;
  parseCoverageOptions = function () {
    const option = optionNamed(["Coverage", "Wrap Coverage", "Trim Level"]);
    return option ? originalParseSelectOptions(option.dataset.optionId, "") : [];
  };
  parseSelectOptions = function (fieldId, label) {
    const items = originalParseSelectOptions(fieldId, label);
    return /^(vinyl type|laminate effect)$/i.test(label || "") ? scoped(items) : items;
  };

  M7Mageworx.setValue = function (fieldId, value, label) {
    const option = this.optionFor(fieldId, label);
    if (choose(option, value)) return true;
    const control = option?.querySelector('select,textarea,input[type="text"],input[type="number"],input[type="tel"]');
    if (!control) return false;
    control.value = value;
    this.emit(control, "input");
    this.emit(control, "change");
    return true;
  };
  M7Mageworx.setRadio = function (fieldId, value, label) {
    return choose(this.optionFor(fieldId, label), value);
  };

  document.addEventListener("click", function (event) {
    const card = event.target.closest("[data-m7-coverage-card]");
    if (!card) return;
    choose(optionNamed(["Coverage", "Wrap Coverage", "Trim Level"]), card.dataset.m7CoverageCard);
    resetScopedChoices();
  }, true);
  document.addEventListener("change", function (event) {
    if (!event.target.matches("[data-m7-coverage]")) return;
    choose(optionNamed(["Coverage", "Wrap Coverage", "Trim Level"]), event.target.value);
    resetScopedChoices();
  }, true);

  async function customerDiscovery() {
    const response = await fetch("https://m7designsco.com/.well-known/openid-configuration");
    if (!response.ok) throw new Error("Shopify login is temporarily unavailable.");
    return response.json();
  }

  function readCustomerAuth() {
    try { return JSON.parse(localStorage.getItem("m7-customer-auth") || "null"); }
    catch (_) { return null; }
  }

  function tokenClaims(token) {
    try {
      const value = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      return JSON.parse(decodeURIComponent(escape(atob(value.padEnd(Math.ceil(value.length / 4) * 4, "=")))));
    } catch (_) { return {}; }
  }

  async function customerAuth() {
    const auth = readCustomerAuth();
    if (!auth) return null;
    if (auth.accessToken && Number(auth.expiresAt || 0) > Date.now() + 60_000) return auth;
    if (!auth.refreshToken) {
      localStorage.removeItem("m7-customer-auth");
      return null;
    }
    try {
      const discovery = await customerDiscovery();
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: auth.refreshToken,
        client_id: "7105a5af-4b93-405e-aa73-a757b3968827"
      });
      const response = await fetch(discovery.token_endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body
      });
      if (!response.ok) throw new Error("Session expired");
      const tokens = await response.json();
      const updated = {
        accessToken: tokens.access_token || auth.accessToken,
        idToken: tokens.id_token || auth.idToken,
        refreshToken: tokens.refresh_token || auth.refreshToken,
        expiresAt: Date.now() + Number(tokens.expires_in || 300) * 1000
      };
      localStorage.setItem("m7-customer-auth", JSON.stringify(updated));
      return updated;
    } catch (_) {
      localStorage.removeItem("m7-customer-auth");
      return null;
    }
  }

  function randomUrlToken(bytes = 32) {
    const data = crypto.getRandomValues(new Uint8Array(bytes));
    return btoa(String.fromCharCode(...data)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  async function sha256Url(value) {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  function accountDesignFromSnapshot(snapshot) {
    const allowed = new Set([
      ...(snapshot.activeZones || []),
      ...Object.keys(snapshot.paints || {}).filter(key => key.startsWith("PART_"))
    ]);
    return {
      v: 4,
      name: snapshot.designName || "Untitled wrap",
      paints: Object.fromEntries(Object.entries(snapshot.paints || {}).filter(([key]) => allowed.has(key))),
      options: snapshot.options || {}
    };
  }

  async function beginCustomerLogin(pendingDesign) {
    if (pendingDesign) localStorage.setItem("m7-customer-pending-design", JSON.stringify(pendingDesign));
    const verifier = randomUrlToken(48);
    const state = randomUrlToken(24);
    const nonce = randomUrlToken(24);
    const returnUrl = new URL(location.href);
    returnUrl.searchParams.delete("code");
    returnUrl.searchParams.delete("state");
    localStorage.setItem("m7-customer-auth-flow", JSON.stringify({
      verifier,
      state,
      nonce,
      returnUrl: returnUrl.href,
      createdAt: Date.now()
    }));
    const discovery = await customerDiscovery();
    const authorize = new URL(discovery.authorization_endpoint);
    authorize.searchParams.set("scope", "openid email customer-account-api:full");
    authorize.searchParams.set("client_id", "7105a5af-4b93-405e-aa73-a757b3968827");
    authorize.searchParams.set("response_type", "code");
    authorize.searchParams.set("redirect_uri", "https://m7designsco.com/pages/m7-design-login");
    authorize.searchParams.set("state", state);
    authorize.searchParams.set("nonce", nonce);
    authorize.searchParams.set("code_challenge", await sha256Url(verifier));
    authorize.searchParams.set("code_challenge_method", "S256");
    authorize.searchParams.set("locale", "en-CA");
    authorize.searchParams.set("region_country", "CA");
    location.href = authorize.href;
  }

  async function accountFetch(path, options = {}) {
    const auth = await customerAuth();
    if (!auth?.accessToken) {
      const error = new Error("Customer login required");
      error.code = "LOGIN_REQUIRED";
      throw error;
    }
    const headers = new Headers(options.headers || {});
    headers.set("Authorization", "Bearer " + auth.accessToken);
    if (auth.idToken) headers.set("X-M7-Customer-Identity", auth.idToken);
    if (options.body) headers.set("Content-Type", "application/json");
    const accountRoot = document.getElementById("m7WrapEditor");
    const accountEndpoint = String(accountRoot?.dataset.m7HandoffEndpoint || "").replace(/\/$/, "");
    if (!/^https:\/\//i.test(accountEndpoint)) throw new Error("The design storage endpoint has not been configured.");
    const response = await fetch(accountEndpoint + path, { ...options, headers });
    const result = await response.json().catch(() => ({}));
    if (response.status === 401) {
      localStorage.removeItem("m7-customer-auth");
      const error = new Error("Customer login required");
      error.code = "LOGIN_REQUIRED";
      throw error;
    }
    if (!response.ok) throw new Error(result.error || "The customer design service failed.");
    return result;
  }

  async function saveAccountSnapshot(snapshot, options = {}) {
    const auth = await customerAuth();
    if (!auth) return null;
    const accountRoot = document.getElementById("m7WrapEditor");
    const productHandle = accountRoot?.dataset.productHandle || "";
    const result = await accountFetch("/api/account/designs", {
      method: "POST",
      body: JSON.stringify({
        designId: "",
        productHandle,
        productTitle: accountRoot?.dataset.productTitle || document.title,
        design: accountDesignFromSnapshot(snapshot)
      })
    });
    sessionStorage.setItem("m7-current-account-design", result.designId);
    sessionStorage.setItem("m7-current-account-product", productHandle);
    if (!options.silent) showAccountNotice("NEW DESIGN SAVED", "This colorway was added to your account without replacing an earlier design.");
    setTimeout(renderUnifiedDesigns, 0);
    return result;
  }

  window.M7SaveAccountSnapshot = saveAccountSnapshot;

  function showAccountNotice(title, message) {
    let notice = document.getElementById("m7AccountNotice");
    if (!notice) {
      notice = document.createElement("div");
      notice.id = "m7AccountNotice";
      notice.innerHTML = "<strong></strong><span></span>";
      document.body.appendChild(notice);
    }
    notice.querySelector("strong").textContent = title;
    notice.querySelector("span").textContent = message;
    notice.classList.add("show");
    clearTimeout(notice._timer);
    notice._timer = setTimeout(() => notice.classList.remove("show"), 3600);
  }

  function ensureAccountUi() {
    if (document.getElementById("m7AccountModal")) return;
    const modal = document.createElement("div");
    modal.id = "m7AccountModal";
    modal.className = "m7-account-modal";
    modal.setAttribute("aria-hidden", "true");
    modal.innerHTML = `<div class="m7-account-dialog" role="dialog" aria-modal="true" aria-labelledby="m7AccountTitle">
      <button type="button" class="m7-account-close" data-m7-account-close aria-label="Close">X</button>
      <small>M7 CUSTOMER ACCOUNT</small><h2 id="m7AccountTitle">Save this design everywhere.</h2>
      <p id="m7AccountIntro">Sign in with Shopify's secure email code to save this design and open it from any device.</p>
      <div id="m7AccountContent"><button type="button" class="m7-account-primary" data-m7-account-login>SIGN IN & SAVE</button><button type="button" data-m7-account-local>ONLY SAVE ON THIS DEVICE</button></div>
    </div>`;
    document.body.appendChild(modal);

    const savedArea = document.getElementById("m7SavedDesigns");
    const accountRoot = document.getElementById("m7WrapEditor");
    const unified = document.createElement("section");
    unified.id = "m7UnifiedDesigns";
    unified.innerHTML = '<button type="button" class="m7-unified-head" data-m7-unified-toggle><span>CLOUD SAVED DESIGNS</span><b>LOADING...</b></button><button type="button" class="m7-unified-save-icon" data-m7-unified-save aria-label="Save current design" title="Save current design"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 3h14l3 3v15H3V3h1zm2 2v5h10V5H6zm0 9v5h12v-5H6z"/></svg></button>';
    if (savedArea?.parentElement) savedArea.insertAdjacentElement("beforebegin", unified);
    else (accountRoot?.querySelector(".m7x-footer") || accountRoot)?.appendChild(unified);

    const libraryModal = document.createElement("div");
    libraryModal.id = "m7UnifiedModal";
    libraryModal.setAttribute("aria-hidden", "true");
    libraryModal.innerHTML = '<div class="m7-unified-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="m7UnifiedModalTitle"><button type="button" class="m7-unified-modal-close" data-m7-unified-close aria-label="Close">X</button><small>M7 CUSTOMER ACCOUNT</small><h2 id="m7UnifiedModalTitle">Saved designs <b>LOADING...</b></h2><p>Open any saved wrap and continue editing it.</p><div class="m7-unified-list"></div></div>';
    document.body.appendChild(libraryModal);
    if (savedArea) {
      savedArea.hidden = true;
      let refreshTimer;
      new MutationObserver(() => {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(renderUnifiedDesigns, 100);
      }).observe(savedArea, { childList: true, subtree: true });
    }
    const closeUnifiedLibrary = () => {
      libraryModal.classList.remove("open");
      libraryModal.setAttribute("aria-hidden", "true");
    };
    const handleUnifiedClick = async event => {
      if (event.target.closest("[data-m7-unified-toggle]")) {
        libraryModal.classList.add("open");
        libraryModal.setAttribute("aria-hidden", "false");
        renderUnifiedDesigns();
      }
      if (event.target === libraryModal || event.target.closest("[data-m7-unified-close]")) closeUnifiedLibrary();
      const deleteCloud = event.target.closest("[data-m7-unified-delete-cloud]");
      if (deleteCloud) {
        event.preventDefault();
        event.stopPropagation();
        if (!confirm(`Delete "${deleteCloud.dataset.m7DesignName || "this design"}" from your account?`)) return;
        deleteCloud.disabled = true;
        try {
          await accountFetch("/api/account/designs/" + encodeURIComponent(deleteCloud.dataset.m7UnifiedDeleteCloud), { method: "DELETE" });
          removeLocalDesignCopies(deleteCloud.dataset.m7DesignName);
          showAccountNotice("DESIGN DELETED", "The saved wrap was removed from your account.");
          await renderUnifiedDesigns();
        } catch (error) {
          deleteCloud.disabled = false;
          alert(error?.message || "The saved design could not be deleted.");
        }
        return;
      }
      const deleteLocal = event.target.closest("[data-m7-unified-delete-local]");
      if (deleteLocal) {
        event.preventDefault();
        event.stopPropagation();
        if (!confirm(`Delete "${deleteLocal.dataset.m7DesignName || "this design"}" from this device?`)) return;
        removeLocalDesignCopies(deleteLocal.dataset.m7DesignName, Number(deleteLocal.dataset.m7UnifiedDeleteLocal));
        showAccountNotice("DESIGN DELETED", "The device-only wrap was removed.");
        await renderUnifiedDesigns();
        return;
      }
      const cloud = event.target.closest("[data-m7-unified-cloud]");
      if (cloud) loadAccountDesign(cloud.dataset.m7UnifiedCloud, cloud.dataset.m7ProductHandle);
      const local = event.target.closest("[data-m7-unified-local]");
      if (local) document.querySelector(`#m7SavedDesigns [data-m7-load="${local.dataset.m7UnifiedLocal}"]`)?.click();
      if (event.target.closest("[data-m7-unified-save]")) {
        const save = document.getElementById("m7WrapEditor")?.querySelector('[data-m7-action="save"]');
        if (save) save.click();
      }
      if (event.target.closest("[data-m7-unified-login]")) {
        const snapshot = window.M7Editor?.getSnapshot?.();
        await beginCustomerLogin(snapshot ? accountDesignFromSnapshot(snapshot) : null);
      }
    };
    unified.addEventListener("click", handleUnifiedClick);
    libraryModal.addEventListener("click", handleUnifiedClick);
    renderUnifiedDesigns();

    modal.addEventListener("click", async event => {
      event.stopPropagation();
      if (event.target === modal || event.target.closest("[data-m7-account-close]")) closeAccountModal();
      if (event.target.closest("[data-m7-account-login]")) {
        event.preventDefault();
        const loginButton = event.target.closest("[data-m7-account-login]");
        const originalText = loginButton.textContent;
        loginButton.disabled = true;
        loginButton.textContent = "CONNECTING TO SHOPIFY...";
        try {
          const snapshot = window.M7Editor?.getSnapshot?.();
          await beginCustomerLogin(snapshot ? accountDesignFromSnapshot(snapshot) : null);
        } catch (error) {
          loginButton.disabled = false;
          loginButton.textContent = originalText;
          document.getElementById("m7AccountIntro").textContent = error?.message || "Shopify sign-in could not be started. Please try again.";
          document.getElementById("m7AccountIntro").style.color = "#ff8585";
        }
      }
      if (event.target.closest("[data-m7-account-local]")) {
        closeAccountModal();
        const save = document.getElementById("m7WrapEditor")?.querySelector('[data-m7-action="save"]');
        if (save) { save.dataset.m7LocalOnce = "true"; save.click(); }
      }
      const load = event.target.closest("[data-m7-account-load]");
      if (load) loadAccountDesign(load.dataset.m7AccountLoad, load.dataset.m7ProductHandle);
      const remove = event.target.closest("[data-m7-account-delete]");
      if (remove && confirm("Delete this saved design from your account?")) {
        await accountFetch("/api/account/designs/" + encodeURIComponent(remove.dataset.m7AccountDelete), { method: "DELETE" });
        await renderAccountLibrary();
        await renderUnifiedDesigns();
      }
    });
  }

  function openAccountModal() {
    ensureAccountUi();
    const modal = document.getElementById("m7AccountModal");
    modal.classList.add("open");
    modal.setAttribute("aria-hidden", "false");
  }

  function closeAccountModal() {
    const modal = document.getElementById("m7AccountModal");
    modal?.classList.remove("open");
    modal?.setAttribute("aria-hidden", "true");
  }

  async function openAccountLibrary() {
    if (!await customerAuth()) {
      openAccountModal();
      return;
    }
    openAccountModal();
    document.getElementById("m7AccountTitle").textContent = "Your saved designs";
    document.getElementById("m7AccountIntro").textContent = "Open a design on this device and continue where you left off.";
    await renderAccountLibrary();
  }

  async function renderAccountLibrary() {
    const content = document.getElementById("m7AccountContent");
    content.innerHTML = '<div class="m7-account-loading">LOADING DESIGNS...</div>';
    try {
      const result = await accountFetch("/api/account/designs");
      const designs = result.designs || [];
      content.innerHTML = designs.length ? designs.map(item => `<article class="m7-account-design">
        <div><strong>${escapeAccountHtml(item.name || "Untitled wrap")}</strong><small>${escapeAccountHtml(item.productTitle || item.productHandle || "M7 Wrap")}</small><time>${new Date(item.updatedAt).toLocaleDateString()}</time></div>
        <button type="button" class="m7-account-primary" data-m7-account-load="${item.designId}" data-m7-product-handle="${escapeAccountHtml(item.productHandle || "")}">OPEN</button>
        <button type="button" class="m7-account-delete" data-m7-account-delete="${item.designId}" aria-label="Delete design">X</button>
      </article>`).join("") : '<div class="m7-account-empty">No cloud designs yet.<br>Close this window and press Save to add your first one.</div>';
    } catch (error) {
      content.innerHTML = `<div class="m7-account-empty">${escapeAccountHtml(error.message)}</div>`;
    }
  }

  async function renderUnifiedDesigns() {
    const host = document.getElementById("m7UnifiedDesigns");
    if (!host) return;
    const list = document.querySelector("#m7UnifiedModal .m7-unified-list");
    if (!list) return;
    const count = host.querySelector(".m7-unified-head b");
    const modalCount = document.querySelector("#m7UnifiedModalTitle b");
    let local = [];
    try { local = JSON.parse(localStorage.getItem("m7-wrap-designs") || "[]"); } catch (_) {}
    let cloud = [];
    let signedIn = false;
    try {
      signedIn = !!await customerAuth();
      if (signedIn) {
        const result = await accountFetch("/api/account/designs");
        cloud = result.designs || [];
      }
    } catch (_) { signedIn = false; }
    const cloudNames = new Set(cloud.map(item => String(item.name || "").trim().toLowerCase()));
    const localOnly = local.map((item, index) => ({ item, index })).filter(entry => !cloudNames.has(String(entry.item.name || "").trim().toLowerCase()));
    const total = cloud.length + localOnly.length;
    count.textContent = total ? `(${total})` : "(0)";
    if (modalCount) modalCount.textContent = total ? `(${total})` : "(0)";
    const cloudHtml = cloud.map(item => `<article class="m7-unified-design"><span><b>${escapeAccountHtml(item.name || "Untitled wrap")}</b><small>${escapeAccountHtml(item.productTitle || item.productHandle || "M7 WRAP")} - ANY DEVICE</small></span><span class="m7-unified-actions"><button type="button" data-m7-unified-cloud="${item.designId}" data-m7-product-handle="${escapeAccountHtml(item.productHandle || "")}">OPEN -></button><button type="button" class="m7-unified-delete" data-m7-unified-delete-cloud="${item.designId}" data-m7-design-name="${escapeAccountHtml(item.name || "Untitled wrap")}" aria-label="Delete saved design" title="Delete saved design">X</button></span></article>`).join("");
    const localHtml = localOnly.map(entry => `<article class="m7-unified-design local"><span><b>${escapeAccountHtml(entry.item.name || "Untitled wrap")}</b><small>THIS DEVICE ONLY</small></span><span class="m7-unified-actions"><button type="button" data-m7-unified-local="${entry.index}">OPEN -></button><button type="button" class="m7-unified-delete" data-m7-unified-delete-local="${entry.index}" data-m7-design-name="${escapeAccountHtml(entry.item.name || "Untitled wrap")}" aria-label="Delete saved design" title="Delete saved design">X</button></span></article>`).join("");
    const loginHtml = signedIn ? "" : '<button type="button" class="m7-unified-login" data-m7-unified-login>SIGN IN TO SEE DESIGNS FROM EVERY DEVICE</button>';
    list.innerHTML = cloudHtml + localHtml + loginHtml + (!total ? '<p class="m7-unified-empty">No saved designs yet.</p>' : "");
  }

  function removeLocalDesignCopies(name, fallbackIndex = -1) {
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem("m7-wrap-designs") || "[]"); } catch (_) {}
    const normalized = String(name || "").trim().toLowerCase();
    saved = normalized
      ? saved.filter(item => String(item?.name || "").trim().toLowerCase() !== normalized)
      : saved.filter((_, index) => index !== fallbackIndex);
    localStorage.setItem("m7-wrap-designs", JSON.stringify(saved));
  }

  function escapeAccountHtml(value) {
    return String(value || "").replace(/[&<>"']/g, character => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[character]));
  }

  function loadAccountDesign(designId, productHandle = "") {
    const url = new URL(location.href);
    const currentHandle = document.getElementById("m7WrapEditor")?.dataset.productHandle || "";
    if (productHandle && productHandle !== currentHandle) {
      url.pathname = "/products/" + encodeURIComponent(productHandle);
    }
    url.searchParams.delete("design");
    url.searchParams.delete("m7cloud");
    url.searchParams.delete("m7short");
    url.searchParams.delete("m7share");
    url.searchParams.delete("m7accountloading");
    url.searchParams.set("account_design", designId);
    location.href = url.href;
  }

  function waitForEditorSnapshot(timeout = 15000) {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        try {
          const snapshot = window.M7Editor?.getSnapshot?.();
          if (snapshot?.paints && Object.keys(snapshot.paints).length) return resolve(snapshot);
        } catch (_) {}
        if (Date.now() - started >= timeout) return reject(new Error("Your design loaded, but the editor was not ready to save it. Press Save Design once more."));
        setTimeout(check, 150);
      };
      check();
    });
  }

  function installMixerTheory() {
    const modal = document.getElementById("m7MixerModal");
    const status = modal?.querySelector(".m7x-mixer-status");
    if (!modal || !status || modal.querySelector(".m7-mixer-theory")) return;
    const theory = document.createElement("section");
    theory.className = "m7-mixer-theory";
    theory.innerHTML = '<small>OPTIONAL COLOR THEORY - OR PICK ANY EXACT SWATCHES BELOW</small><div><button type="button" data-m7-theory="complementary">COMPLEMENTARY</button><button type="button" data-m7-theory="analogous">ANALOGOUS</button><button type="button" data-m7-theory="triadic">TRIADIC</button><button type="button" data-m7-theory="neutral">NEUTRAL + POP</button><button type="button" data-m7-theory="impact">HIGH IMPACT</button></div>';
    status.insertAdjacentElement("afterend", theory);
    let anchor = "";
    modal.addEventListener("click", event => {
      const swatch = event.target.closest("[data-m7-mixer-color]");
      if (swatch) anchor = swatch.dataset.m7MixerColor;
      const button = event.target.closest("[data-m7-theory]");
      if (!button) return;
      const available = [...modal.querySelectorAll("[data-m7-mixer-color]")].map(item => item.dataset.m7MixerColor).filter(Boolean);
      const lastChip = [...modal.querySelectorAll("[data-m7-mixer-remove]")].slice(-1)[0];
      const source = colorChannels(anchor || lastChip?.style.backgroundColor);
      if (!source || !available.length) return alert("Choose an anchor swatch first.");
      const sourceHsl = rgbToHsl(source);
      const nearest = (hue, saturation, lightness) => available.reduce((best, color) => {
        const hsl = rgbToHsl(colorChannels(color));
        const hueDistance = Math.min(Math.abs(hsl[0] - hue), 360 - Math.abs(hsl[0] - hue));
        const score = hueDistance * hueDistance * 3 + Math.pow(hsl[1] - saturation, 2) * 9000 + Math.pow(hsl[2] - lightness, 2) * 5000;
        return !best || score < best.score ? { color, score } : best;
      }, null).color;
      const tone = (offset, saturation = Math.max(.62, sourceHsl[1]), lightness = Math.max(.38, Math.min(.62, sourceHsl[2]))) => nearest((sourceHsl[0] + offset + 360) % 360, saturation, lightness);
      const recipes = {
        complementary: [tone(0), tone(0), tone(180), tone(180), tone(0)],
        analogous: [tone(-35), tone(0), tone(0), tone(35), tone(35)],
        triadic: [tone(0), tone(0), tone(120), tone(240), tone(240)],
        neutral: [tone(0), tone(0), nearest(0, 0, .08), nearest(0, 0, .5), nearest(0, 0, .94)],
        impact: [tone(0, .9, .5), tone(0, .9, .5), tone(180, .9, .5), tone(120, .85, .5), tone(240, .85, .5), nearest(0, 0, .06), nearest(0, 0, .96)]
      };
      modal.querySelector("[data-m7-mixer-clear]")?.click();
      recipes[button.dataset.m7Theory].forEach(color => {
        [...modal.querySelectorAll("[data-m7-mixer-color]")].find(item => item.dataset.m7MixerColor === color)?.click();
      });
      anchor = recipes[button.dataset.m7Theory][0];
      showAccountNotice("THEORY PALETTE READY", `${button.textContent.trim()} colors added to the Mixer.`);
    });
    const style = document.createElement("style");
    style.textContent = '.m7-mixer-theory{display:grid!important;visibility:visible!important;opacity:1!important;position:relative;z-index:4;gap:8px;padding:11px 0}.m7-mixer-theory>small{color:#9da5ad;font-size:9px;font-weight:900;letter-spacing:.8px}.m7-mixer-theory>div{display:flex;flex-wrap:wrap;gap:6px}.m7-mixer-theory button{padding:8px 10px;border:1px solid #39414a;border-radius:5px;background:#151a20;color:#dce1e6;font-size:9px;font-weight:900;letter-spacing:.45px;cursor:pointer}.m7-mixer-theory button:hover{border-color:#b9ff19;color:#b9ff19}.m7-unified-design .m7-unified-actions{display:flex;align-items:center;gap:7px;flex:none}.m7-unified-actions button{padding:8px;border:0;background:transparent;color:#b9ff19;font-size:10px;font-weight:900;cursor:pointer}.m7-unified-actions .m7-unified-delete{width:30px;height:30px;border:1px solid #56363a;border-radius:5px;color:#ff7d84;font-size:18px;line-height:1}.m7-unified-actions .m7-unified-delete:hover{border-color:#ff7d84;background:#2a171a}@media(max-width:600px){.m7-mixer-theory button{flex:1 1 calc(50% - 6px);min-height:38px}.m7-unified-design .m7-unified-actions{gap:2px}}';
    document.head.appendChild(style);
  }

  function colorChannels(value) {
    const text = String(value || "").trim();
    if (text.startsWith("#")) {
      const hex = text.length === 4 ? [...text.slice(1)].map(value => value + value).join("") : text.slice(1, 7);
      return [0, 2, 4].map(index => parseInt(hex.slice(index, index + 2), 16));
    }
    const channels = text.match(/[\d.]+/g);
    return channels?.length >= 3 ? channels.slice(0, 3).map(Number) : null;
  }

  function rgbToHsl(rgb) {
    const [red, green, blue] = rgb.map(value => value / 255);
    const maximum = Math.max(red, green, blue), minimum = Math.min(red, green, blue), delta = maximum - minimum;
    let hue = 0;
    if (delta) hue = maximum === red ? 60 * (((green - blue) / delta) % 6) : maximum === green ? 60 * ((blue - red) / delta + 2) : 60 * ((red - green) / delta + 4);
    if (hue < 0) hue += 360;
    const lightness = (maximum + minimum) / 2;
    return [hue, delta ? delta / (1 - Math.abs(2 * lightness - 1)) : 0, lightness];
  }

  function installAccountDesigns() {
    const params = new URLSearchParams(location.search);
    const loginComplete = params.get("m7_login") === "complete";
    if (loginComplete) {
      try {
        const pending = JSON.parse(localStorage.getItem("m7-customer-pending-design") || "null");
        if (pending?.paints && Object.keys(pending.paints).length) {
          const restoreUrl = new URL(location.href);
          restoreUrl.searchParams.delete("m7_login");
          restoreUrl.searchParams.set("design", btoa(unescape(encodeURIComponent(JSON.stringify(pending)))));
          restoreUrl.searchParams.set("m7_account_save", "pending");
          location.replace(restoreUrl.pathname + restoreUrl.search + restoreUrl.hash);
          return;
        }
      } catch (_) {}
    }
    const accountDesignId = params.get("account_design") || "";
    const restoredId = params.get("m7cloud") || "";
    if (restoredId && params.get("design")) {
      sessionStorage.setItem("m7-current-account-design", restoredId);
      sessionStorage.setItem("m7-current-account-product", document.getElementById("m7WrapEditor")?.dataset.productHandle || "");
      // Keep the design marker in the URL while this editor is open. The SVG
      // can initialize more than once; removing it here made later passes
      // mistake a restored design for the product default colorway.
    } else if (/^M7D-[A-Z0-9]{8}$/.test(accountDesignId) && /^https:\/\//i.test(String(document.getElementById("m7WrapEditor")?.dataset.m7HandoffEndpoint || ""))) {
      const loadingUrl = new URL(location.href);
      loadingUrl.searchParams.delete("account_design");
      loadingUrl.searchParams.set("m7accountloading", accountDesignId);
      history.replaceState(null, "", loadingUrl.pathname + loadingUrl.search + loadingUrl.hash);
      accountFetch("/api/account/designs/" + encodeURIComponent(accountDesignId)).then(record => {
        let design = record.design;
        for (let depth = 0; depth < 3 && typeof design === "string"; depth++) {
          try { design = JSON.parse(design); } catch (_) { break; }
        }
        if (design?.design && !design?.paints) design = design.design;
        if (!design?.paints || !Object.keys(design.paints).length) {
          throw new Error("This saved design does not contain editable color data.");
        }
        sessionStorage.setItem("m7-account-design-payload", JSON.stringify({ designId: accountDesignId, design }));
        const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(design))));
        const loadUrl = new URL(location.href);
        loadUrl.searchParams.delete("m7accountloading");
        loadUrl.searchParams.set("design", encoded);
        loadUrl.searchParams.set("m7cloud", accountDesignId);
        location.replace(loadUrl.pathname + loadUrl.search + loadUrl.hash);
      }).catch(async error => {
        if (error.code === "LOGIN_REQUIRED") await beginCustomerLogin(null);
        else alert(error.message);
      });
    }

    addEventListener("DOMContentLoaded", async function () {
      ensureAccountUi();
      const currentUrl = new URL(location.href);
      const shouldSavePending = currentUrl.searchParams.get("m7_account_save") === "pending";
      if (loginComplete || shouldSavePending) {
        const clean = new URL(location.href);
        clean.searchParams.delete("m7_login");
        clean.searchParams.delete("m7_account_save");
        history.replaceState(null, "", clean.pathname + clean.search + clean.hash);
        try {
          const pending = JSON.parse(localStorage.getItem("m7-customer-pending-design") || "null");
          if (pending) {
            const restored = await waitForEditorSnapshot();
            await saveAccountSnapshot(restored);
            localStorage.removeItem("m7-customer-pending-design");
            showAccountNotice("DESIGN RESTORED & SAVED", "Your colors are back and this design is now available from your account.");
          }
        } catch (error) {
          showAccountNotice("DESIGN RESTORED - SAVE NEEDED", error?.message || "Press Save Design once more to finish saving it to your account.");
        }
      }
    });

    document.addEventListener("click", async function (event) {
      const button = event.target.closest('[data-m7-action="save"]');
      if (!button || button.dataset.m7LocalOnce === "true") {
        if (button) delete button.dataset.m7LocalOnce;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      const snapshot = window.M7Editor?.getSnapshot?.();
      if (!snapshot) return alert("The design is not ready to save yet.");
      if (!await customerAuth()) {
        openAccountModal();
        return;
      }
      button.disabled = true;
      try { await saveAccountSnapshot(snapshot); }
      catch (error) { alert(error.message); }
      finally { button.disabled = false; }
    }, true);
  }

  function installShortShareLinks() {
    const params = new URLSearchParams(location.search);
    const incoming = params.get("design") || "";
    const shortId = /^M7S-[A-Z0-9]+$/.test(incoming) ? incoming : "";
    const restoredShortId = params.get("m7short") || "";

    if (restoredShortId && incoming && !/^M7S-/.test(incoming)) {
      addEventListener("DOMContentLoaded", function () {
        const clean = new URL(location.href);
        clean.searchParams.set("design", restoredShortId);
        clean.searchParams.delete("m7short");
        history.replaceState(null, "", clean.pathname + clean.search + clean.hash);
      });
    } else if (shortId && isConfigured()) {
      const loadingUrl = new URL(location.href);
      loadingUrl.searchParams.delete("design");
      loadingUrl.searchParams.set("m7share", shortId);
      history.replaceState(null, "", loadingUrl.pathname + loadingUrl.search + loadingUrl.hash);
      fetch(endpoint() + "/api/shares/" + encodeURIComponent(shortId))
        .then(response => response.ok ? response.json() : Promise.reject(new Error("Shared design not found")))
        .then(design => {
          const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(design))));
          const loadUrl = new URL(location.href);
          loadUrl.searchParams.delete("m7share");
          loadUrl.searchParams.set("design", encoded);
          loadUrl.searchParams.set("m7short", shortId);
          location.replace(loadUrl.pathname + loadUrl.search + loadUrl.hash);
        })
        .catch(error => {
          const failedUrl = new URL(location.href);
          failedUrl.searchParams.delete("m7share");
          failedUrl.searchParams.set("design", shortId);
          history.replaceState(null, "", failedUrl.pathname + failedUrl.search + failedUrl.hash);
          alert(error.message || "This shared design could not be loaded.");
        });
    }

    document.addEventListener("click", async function (event) {
      const button = event.target.closest('[data-m7-action="share"]');
      if (!button || !isConfigured() || shortId) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.dataset.m7Sharing === "true") return;
      button.dataset.m7Sharing = "true";
      button.disabled = true;
      const label = button.querySelector("span");
      const originalText = label?.textContent || "SHARE";
      if (label) label.textContent = "SAVING";
      try {
        const snapshot = window.M7Editor?.getSnapshot?.();
        if (!snapshot) throw new Error("The design is not ready to share yet.");
        const allowed = new Set([
          ...(snapshot.activeZones || []),
          ...Object.keys(snapshot.paints || {}).filter(key => key.startsWith("PART_"))
        ]);
        const paints = Object.fromEntries(Object.entries(snapshot.paints || {}).filter(([key]) => allowed.has(key)));
        const response = await fetch(endpoint() + "/api/shares", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            productHandle: root()?.dataset.productHandle || "",
            design: { v: 4, name: snapshot.designName, paints, options: snapshot.options || {} }
          })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.shareId) throw new Error(result.error || "The share link could not be created.");
        const shareUrl = new URL(location.origin + location.pathname);
        shareUrl.searchParams.set("design", result.shareId);
        try {
          await navigator.clipboard.writeText(shareUrl.href);
          if (label) label.textContent = "COPIED";
        } catch (_) {
          prompt("Copy this design link", shareUrl.href);
        }
      } catch (error) {
        alert(error.message || "The share link could not be created.");
      } finally {
        setTimeout(function () {
          button.disabled = false;
          button.dataset.m7Sharing = "false";
          if (label) label.textContent = originalText;
        }, 1400);
      }
    }, true);
  }

  window.M7InstallMixerTheory = installMixerTheory;
  installMixerTheory();
  addEventListener("DOMContentLoaded", installMixerTheory);
  setTimeout(installMixerTheory, 500);
  setTimeout(installMixerTheory, 1500);
  document.addEventListener("pointerdown", event => {
    if (event.target.closest('[data-m7-action="mixer"]')) setTimeout(installMixerTheory, 0);
  }, true);
  document.addEventListener("shopify:section:load", () => setTimeout(installMixerTheory, 100));
  const mixerTheoryObserver = new MutationObserver(() => {
    installMixerTheory();
    if (document.querySelector(".m7-mixer-theory")) mixerTheoryObserver.disconnect();
  });
  mixerTheoryObserver.observe(document.documentElement, { childList: true, subtree: true });

  installAccountDesigns();
  installShortShareLinks();

  const style = document.createElement("style");
  style.textContent = ".m7x-finish-grid{gap:10px!important}.m7x-finish-card{min-height:0!important}.m7x-finish-texture{height:54px!important;min-height:54px!important}.m7x-finish-body{padding:9px 12px!important}.m7x-finish-body small{font-size:10px!important;line-height:1.3!important}#m7CloudDesignsButton{width:100%;margin-top:9px;padding:11px;border:1px solid #b9ff19;border-radius:6px;background:#111419;color:#b9ff19;font-size:11px;font-weight:900;letter-spacing:.7px;cursor:pointer}.m7-account-modal{display:none;position:fixed;inset:0;z-index:2147483600;align-items:center;justify-content:center;padding:18px;background:#05070bd9}.m7-account-modal.open{display:flex}.m7-account-dialog{position:relative;width:min(560px,100%);max-height:85vh;overflow:auto;padding:28px;border:1px solid #353b42;border-radius:12px;background:#0d1015;color:#fff;box-shadow:0 20px 80px #000}.m7-account-dialog>small{color:#b9ff19;font-weight:900;letter-spacing:1.4px}.m7-account-dialog h2{margin:8px 35px 8px 0;font-size:28px}.m7-account-dialog p{color:#aeb4bb;line-height:1.5}.m7-account-close{position:absolute;right:14px;top:12px;border:0;background:transparent;color:#fff;font-size:28px;cursor:pointer}.m7-account-dialog button{padding:12px 14px;border:1px solid #3d444c;border-radius:6px;background:#171b21;color:#fff;font-weight:900;cursor:pointer}.m7-account-dialog .m7-account-primary{background:#b9ff19;color:#080a0c;border-color:#b9ff19}.m7-account-dialog #m7AccountContent{display:grid;gap:9px;margin-top:18px}.m7-account-design{display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:9px;padding:12px;border:1px solid #30363e;border-radius:7px;background:#14181e}.m7-account-design div{display:grid;gap:3px}.m7-account-design small,.m7-account-design time{color:#8e969f;font-size:11px}.m7-account-design .m7-account-delete{padding:6px 10px;color:#ff7777}.m7-account-empty,.m7-account-loading{padding:28px;text-align:center;color:#aeb4bb}#m7AccountNotice{position:fixed;right:18px;bottom:18px;z-index:2147483640;display:grid;gap:3px;max-width:360px;padding:14px 17px;border:1px solid #b9ff19;border-radius:7px;background:#0b0e12;color:#fff;opacity:0;transform:translateY(20px);pointer-events:none;transition:.2s}#m7AccountNotice.show{opacity:1;transform:none}#m7AccountNotice strong{color:#b9ff19;font-size:12px;letter-spacing:.8px}#m7AccountNotice span{font-size:12px;color:#c4cad0}@media(max-width:600px){.m7-account-dialog{padding:22px 16px}.m7-account-dialog h2{font-size:23px}.m7-account-design{grid-template-columns:1fr auto}.m7-account-design .m7-account-delete{grid-column:2}}";
  document.head.appendChild(style);

  const unifiedStyle = document.createElement("style");
  unifiedStyle.textContent = '#m7SavedDesigns,#m7CloudDesignsButton{display:none!important}#m7UnifiedDesigns{width:100%;margin-top:10px;border:1px solid #3b434c;border-radius:7px;background:#101419;overflow:hidden}#m7UnifiedDesigns .m7-unified-head{display:flex;width:100%;align-items:center;justify-content:space-between;padding:13px 15px;border:0;background:#14191f;color:#b9ff19;font-size:12px;font-weight:900;letter-spacing:.7px;cursor:pointer}#m7UnifiedDesigns .m7-unified-list{display:none;max-height:310px;overflow:auto;padding:8px}#m7UnifiedDesigns.open .m7-unified-list{display:grid;gap:7px}.m7-unified-design{display:flex;width:100%;align-items:center;justify-content:space-between;gap:10px;padding:11px 12px;border:1px solid #303842;border-radius:6px;background:#171c22;color:#fff;text-align:left;cursor:pointer}.m7-unified-design span{display:grid;gap:3px;min-width:0}.m7-unified-design b{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px}.m7-unified-design small{color:#b9ff19;font-size:9px;font-weight:900;letter-spacing:.6px}.m7-unified-design.local small{color:#89919a}.m7-unified-design strong{flex:none;color:#b9ff19;font-size:10px}.m7-unified-login{padding:12px;border:1px solid #b9ff19;border-radius:6px;background:#b9ff19;color:#080a0c;font-size:10px;font-weight:900;cursor:pointer}.m7-unified-empty{margin:0;padding:16px;text-align:center;color:#929aa3;font-size:11px}#ShopifyChat,#shopify-chat,shopify-chat,iframe#dummy-chat-button-iframe,iframe[title*="chat widget" i],iframe[title*="Shopify Inbox" i],iframe[title*="Klaviyo" i],body>div.needsclick,body>div[class*="kl-private-reset-css"]{display:none!important}@media(max-width:600px){#m7UnifiedDesigns{position:relative;z-index:20;margin-bottom:18px}#m7UnifiedDesigns .m7-unified-list{max-height:260px}.m7-unified-design{min-height:58px}#m7AccountNotice{right:10px!important;bottom:10px!important;left:10px!important;max-width:none!important}}';
document.head.appendChild(unifiedStyle);

const mobileFixStyle = document.createElement("style");
mobileFixStyle.textContent = `
/* Editor-only floating widget cleanup. */
#ShopifyChat,
#shopify-chat,
shopify-chat,
iframe#dummy-chat-button-iframe,
iframe[title*="chat widget" i],
iframe[title*="Shopify Inbox" i],
iframe[title*="Klaviyo" i],
body .needsclick,
body [class*="kl-private-reset-css"],
body [data-testid*="klaviyo" i] {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
}

/* Let the unified library show every design instead of clipping after two cards. */
#m7UnifiedDesigns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 46px;
  align-items: stretch;
}

.m7-unified-save-icon {
  display: grid;
  place-items: center;
  width: 46px;
  min-width: 46px;
  padding: 0;
  border: 0;
  border-left: 1px solid #3b434c;
  background: #14191f;
  color: #b9ff19;
  cursor: pointer;
}

.m7-unified-save-icon svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

#m7UnifiedModal {
  display: none;
  position: fixed;
  inset: 0;
  z-index: 2147483620;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(5, 7, 11, .86);
  backdrop-filter: blur(4px);
}

#m7UnifiedModal.open {
  display: flex;
}

.m7-unified-modal-dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(620px, 100%);
  max-height: min(82vh, 760px);
  padding: 25px;
  border: 1px solid #3b434c;
  border-radius: 12px;
  background: #0d1015;
  color: #fff;
  box-shadow: 0 24px 90px #000;
}

.m7-unified-modal-dialog > small {
  color: #b9ff19;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 1.3px;
}

.m7-unified-modal-dialog h2 {
  margin: 6px 38px 3px 0;
  font-size: 25px;
}

.m7-unified-modal-dialog h2 b {
  color: #b9ff19;
}

.m7-unified-modal-dialog > p {
  margin: 0 0 14px;
  color: #939ba4;
  font-size: 12px;
}

.m7-unified-modal-close {
  position: absolute;
  top: 10px;
  right: 13px;
  padding: 3px 8px;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 28px;
  cursor: pointer;
}

#m7UnifiedModal .m7-unified-list {
  display: grid;
  gap: 8px;
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 2px 5px 2px 0;
}

/* Keep Quick Style history and mixer controls compact and fully visible. */
.m7x-back {
  height: 30px !important;
  min-height: 30px !important;
  padding: 4px 6px !important;
  font-size: 9px !important;
  line-height: 1 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.m7x-random[data-m7-action="mixer"] {
  min-height: 40px !important;
  padding: 7px 6px !important;
  font-size: 9px !important;
  line-height: 1.15 !important;
}

@media (min-width: 901px) {
  .m7x-scroll.presets > .m7x-copy {
    width: 215px !important;
  }
}

@media (max-width: 600px) {
  #m7UnifiedModal {
    align-items: flex-end;
    padding: 10px;
  }

  .m7-unified-modal-dialog {
    width: 100%;
    max-height: 86vh;
    padding: 21px 15px 15px;
    border-radius: 12px 12px 7px 7px;
  }

  .m7-unified-modal-dialog h2 {
    font-size: 22px;
  }

  #m7UnifiedModal .m7-unified-list {
    padding-right: 3px;
  }

  .m7x-back {
    height: 28px !important;
    min-height: 28px !important;
  }

  .m7x-random[data-m7-action="mixer"] {
    min-height: 38px !important;
  }
}
`;
document.head.appendChild(mobileFixStyle);
})();
